import { ChangesDetection } from "../../../../change-detection";
import { ObservationTextRule } from "../../observation/domain/observation-text-rule.model";
import { E_FlexDirectionResponse } from "../../box-page/api/box-layout-config.response";
import { E_FlexDirection } from "../../box-page/domain/box-layout-config.model";
import {
    I_CardConfigResponse,
    I_CellConfig,
    I_DialogConfig,
    I_IconValueList,
    I_RowCardConfigResponse
} from "../api/card-config.response";
import { C_ChartSettings } from "../../charts/domain/chart-cell-config.model";
import { signal } from "@angular/core";
import { distinctUntilChanged, filter, map, Observable, of, shareReplay, startWith, switchMap } from "rxjs";
import { E_ChangeAction } from "../../../../../enums/change-action.enum";
import { E_WebDisplayCellType, E_WebDisplayCellSubtype } from "../../../../../enums/smart-display/cell-type.enum";

export class C_CardInfoList extends ChangesDetection<T_CardInfoListChange> {
    private list: Map<string, C_CardInfo> = new Map<string, C_CardInfo>();
    private cachedSubs = new Map<string, Observable<C_CardInfo | undefined>>();
    constructor() {
        super();
    }

    size(): number {
        return this.list.size;
    }

    getValues(): C_CardInfo[] {
        return Array.from(this.list.values());
    }

    has(id: string): boolean {
        return this.list.has(id);
    }
    set(layout: I_CardConfigResponse): void {
        if (!layout.id) return;

        if (this.list.has(layout.id)) {
            this.list.get(layout.id)!.initialize(layout);
            this.forceUpdateDetection({
                action: E_ChangeAction.Update,
                cardId: layout.id,
                data: layout
            });
        } else {
            const newCard = new C_CardInfo(layout);
            this.list.set(layout.id, newCard);
            this.forceUpdateDetection({
                action: E_ChangeAction.Add,
                cardId: layout.id,
                data: newCard
            });
        }
    }

    get(id: string): C_CardInfo | undefined {
        return this.list.get(id);
    }

    setCardsBulk(cardList: I_CardConfigResponse[]) {
        const affected = new Set<string>();

        cardList.forEach(card => {
            if (!card.id) return;
            affected.add(card.id);

            const current = this.list.get(card.id);
            if (current) {
                current.initialize(card);
            } else {
                this.list.set(card.id, new C_CardInfo(card));
            }
        });

        this.forceUpdateDetection({ action: E_ChangeAction.AddBulk, affected });
    }

    delete(id: string): void {
        if (this.list.has(id)) {
            this.list.delete(id);
            this.forceUpdateDetection({
                action: E_ChangeAction.Delete,
                cardId: id
            });
        }
    }

    reset(): void {
        this.list.clear();
        this.forceUpdateDetection({ action: E_ChangeAction.Reset });
    }

    getLiveCardInfo(cardId: string): Observable<C_CardInfo | undefined> {
        const cacheKey = cardId;
        if (this.cachedSubs.has(cacheKey)) return this.cachedSubs.get(cacheKey)!;

        const stream$ = this.getForcedChanges().pipe(
            startWith(null),
            filter((change) => {
                if (!change) return true; // Carga inicial
                if (change.action === E_ChangeAction.Reset) return true;
                if (change.action === E_ChangeAction.AddBulk) return change.affected.has(cardId);

                return change.cardId === cardId;
            }),
            map(() => this.list.get(cardId)),
            distinctUntilChanged(),
            switchMap(item => {
                if (!item) return of(undefined);

                // Escucha los cambios internos (como actualizaciones en las filas/rows de la tarjeta)
                return item.getForcedChanges().pipe(
                    startWith(item),
                    map(() => item)
                );
            }),
            shareReplay({ bufferSize: 1, refCount: true })
        );

        this.cachedSubs.set(cacheKey, stream$);
        return stream$;
    }

    clearSubs() {
        this.cachedSubs.clear();
        this.forceUpdateDetection({ action: E_ChangeAction.Reset });
    }

}

export type T_CardInfoListChange =
    | { action: E_ChangeAction.Add; cardId: string; data: C_CardInfo }
    | { action: E_ChangeAction.Update; cardId: string; data: I_CardConfigResponse }
    | { action: E_ChangeAction.Delete; cardId: string }
    | { action: E_ChangeAction.AddBulk; affected: Set<string> }
    | { action: E_ChangeAction.Reset };

export class C_CardInfo extends ChangesDetection {
    id?: string;
    rows: C_RowCardInfoList = new C_RowCardInfoList();

    constructor(data?: I_CardConfigResponse) {
        super();
        this.initialize(data);
    }

    initialize(data?: I_CardConfigResponse) {
        if (!data) return;
        if (data.id) this.id = data.id;
        if (data.rows) {
            data.rows.forEach((row, index) => {
                row.id = row.id ?? index + '';
                this.rows.addNewRow(row);
            });
        };
        this.forceUpdateDetection();
    }
}

export class C_RowCardInfoList extends ChangesDetection {

    list: {
        [rowId: string]: C_RowCardInfo;
    } = {}

    constructor(info?: I_RowCardConfigResponse) {
        super();
        if (!info) return;
        this.addNewRow(info);
    }

    addNewRow(info: I_RowCardConfigResponse) {
        if (info?.id != null) this.list[info.id] = new C_RowCardInfo(info);
    }

}

export class C_RowCardInfo extends ChangesDetection {

    cells: C_CellConfig[] = [];
    rows: C_CellConfig[] = [];
    type?: E_WebDisplayCellType;
    growPriority?: number;
    border?: string;
    borderRadius?: string;
    bgColor?: string;
    title?: string;
    paddingTop?: string;
    paddingBottom?: string;
    paddingLeft?: string;
    paddingRight?: string;
    direction?: null | 'row' | 'column' | 'row-reverse' | 'column-reverse';
    constructor(info?: I_RowCardConfigResponse) {
        super();
        if (!info) return;
        if (info.cells) info?.cells.forEach((cell) => {
            this.cells.push(new C_CellConfig(cell));
        });
        if (info.rows) info?.rows.forEach((row) => {
            this.rows.push(new C_CellConfig(row));
        });
        if (info.type) this.type = info.type;
        if (info.title) this.title = info.title;
        if (info.borderRadius) this.borderRadius = info.borderRadius;
        if (info.border) this.border = info.border;
        if (info.hasOwnProperty('growPriority')) this.growPriority = info.growPriority;
        if (info.hasOwnProperty('bgColor')) this.bgColor = info.bgColor;
        if (info.hasOwnProperty('paddingTop')) this.paddingTop = info.paddingTop;
        if (info.hasOwnProperty('paddingBottom')) this.paddingBottom = info.paddingBottom;
        if (info.hasOwnProperty('paddingLeft')) this.paddingLeft = info.paddingLeft;
        if (info.hasOwnProperty('paddingRight')) this.paddingRight = info.paddingRight;
        if (info.hasOwnProperty('direction')) this.direction = this.evaluateDirection(info.direction!);
    }

    evaluateDirection(dir: E_FlexDirectionResponse) {
        switch (dir) {
            case E_FlexDirectionResponse.RowReverse:
                return "row-reverse";
            case E_FlexDirectionResponse.ColumnReverse:
                return "column-reverse";
            case E_FlexDirectionResponse.Row:
                return "row";
            case E_FlexDirectionResponse.Column:
            default:
                return "row";
        }
    }
}

export class C_CellConfig extends ChangesDetection {
    type?: E_WebDisplayCellType;
    cells?: C_CellConfig[] = [];
    chartSettings?: C_ChartSettings;
    subType?: E_WebDisplayCellSubtype;
    growPriority?: number;
    name?: string;
    title?: string;
    valuePath?: string;
    icon?: {
        category: string;
        name: string;
        color?: string;
        size?: string;
    };
    showTitle?: boolean = true;
    showArrow: boolean = false;
    showUnits: boolean = false;
    showDescription: boolean = false;
    border?: string;
    borderRadius?: string;
    paddingTop?: string;
    paddingBottom?: string;
    paddingLeft?: string;
    paddingRight?: string;
    bgColor?: string;
    valuePathNested?: string;
    valuePathKey?: string[];
    iconValueList?: I_IconValueList[];
    dialogConfig?: I_DialogConfig;
    observationName: string[] = [];
    direction: E_FlexDirection = E_FlexDirection.Row;
    graphConf?: string;
    textRules: ObservationTextRule[] = [];
    isStatic: boolean = false;
    minWidth?: string;
    minHeight?: string;
    maxWidth?: string;
    gap?: string;
    fontSize?: string;
    isCollapsable: boolean = false;
    private _collapsedStates = signal<Record<string, boolean>>({});
    constructor(data?: I_CellConfig) {
        super();
        this.initialize(data)
    }

    initialize(data?: I_CellConfig) {
        if (!data) return;
        if (data.type) this.type = data.type;
        if (data.chartSettings) this.chartSettings ? this.chartSettings.initialize(data.chartSettings) : this.chartSettings = new C_ChartSettings(data.chartSettings);
        if (data.subType) this.subType = data.subType;
        if (data.growPriority != null && data.growPriority != undefined) this.growPriority = data.growPriority;
        if (data.icon != null) this.icon = data.icon;
        if (data.showTitle != null) this.showTitle = data.showTitle;
        if (data.maxWidth) this.maxWidth = data.maxWidth;
        if (data.name) this.name = data.name;
        if (data.title) this.title = data.title;
        if (data.valuePath) this.valuePath = data.valuePath;
        if (data.borderRadius) this.borderRadius = data.borderRadius;
        if (data.border) this.border = data.border;
        if (data.paddingTop) this.paddingTop = data.paddingTop;
        if (data.paddingBottom) this.paddingBottom = data.paddingBottom;
        if (data.paddingLeft) this.paddingLeft = data.paddingLeft;
        if (data.paddingRight) this.paddingRight = data.paddingRight;
        if (data.iconValueList) this.iconValueList = data.iconValueList;
        if (data.valuePathNested) this.valuePathNested = data.valuePathNested;
        if (data.valuePathKey) this.valuePathKey = data.valuePathKey;
        if (data.showDescription) this.showDescription = data.showDescription;
        if (data.showUnits) this.showUnits = data.showUnits;
        if (data.minWidth) this.minWidth = data.minWidth;
        if (data.minHeight) this.minHeight = data.minHeight;
        if (data.dialogConfig) this.dialogConfig = data.dialogConfig;
        if (data.hasOwnProperty('isCollapsable')) this.isCollapsable = data.isCollapsable
        if (data.cells) {
            this.cells = [];
            data.cells.forEach(c => this.cells?.push(new C_CellConfig(c)));
        }
        if (data.observationName) this.observationName = data.observationName;
        if (data.graphConf) this.graphConf = data.graphConf;
        if (data.bgColor) this.bgColor = data.bgColor;
        if (data.showArrow != null) this.showArrow = data.showArrow;
        if (data.textRules) {
            this.textRules = [];
            data.textRules.forEach((tr) => {
                this.textRules.push(new ObservationTextRule(tr));
            });
        }
        if (data.direction) this.direction = this.evaluateDirection(data.direction);
        if (data.isStatic) this.isStatic = !!data.isStatic;
        if (data.gap) this.gap = data.gap;
        if (data.fontSize) {
            this.fontSize = data.fontSize;
        }
        this.forceUpdateDetection();
    }

    evaluateDirection(dir: E_FlexDirectionResponse) {
        switch (dir) {
            case E_FlexDirectionResponse.RowReverse:
                return E_FlexDirection.RowReverse;
            case E_FlexDirectionResponse.ColumnReverse:
                return E_FlexDirection.ColumnReverse;
            case E_FlexDirectionResponse.Row:
                return E_FlexDirection.Row;
            case E_FlexDirectionResponse.Column:
            default:
                return E_FlexDirection.Column;
        }
    }

    isCollapsed(id: string): boolean {
        const states = this._collapsedStates();
        return states[id] !== undefined ? states[id] : true;
    }

    toggleCollapse(id: string): void {
        this._collapsedStates.update(states => {
            const currentState = states[id] !== undefined ? states[id] : true;
            return {
                ...states,
                [id]: !currentState
            };
        });
    }

}
